import { AnswerHeadline } from "@nimit9/signet-ui/components/answer-headline"
import { Button } from "@nimit9/signet-ui/components/button"
import { Card } from "@nimit9/signet-ui/components/card"
import { EmptyState } from "@nimit9/signet-ui/components/empty-state"
import { Input } from "@nimit9/signet-ui/components/input"
import { RatioProgress, RatioText } from "@nimit9/signet-ui/components/privacy-mask"
import { ResponsiveSheet } from "@nimit9/signet-ui/components/responsive-sheet"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nimit9/signet-ui/components/select"
import { Skeleton } from "@nimit9/signet-ui/components/skeleton"
import { StatStrip } from "@nimit9/signet-ui/components/stat-strip"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import type { Debt, DebtsResponse } from "@paisa/types"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useId, useState } from "react"
import { z } from "zod"
import { Amount } from "@/components/common/amount"
import { FormActions, FormField } from "@/components/common/form-field"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { useCurrencyFormat } from "@/hooks/use-currency-format"
import { useOwnerLabels } from "@/hooks/use-owner-labels"
import { renderAnswerAmount, toSignetHeadline } from "@/lib/answer-headline"
import { api } from "@/lib/api"
import { useUIStore } from "@/store/ui"
import { buildDebtHeadline, isLiability } from "./-headline"

export const Route = createFileRoute("/debt/")({
  component: DebtPage,
})

const DEBT_TYPE_LABELS: Record<string, string> = {
  personal_loan: "Personal Loan",
  home_loan: "Home Loan",
  car_loan: "Car Loan",
  education_loan: "Education Loan",
  credit_card: "Credit Card",
  other: "Other",
}

const DEBT_TYPE_ICONS: Record<string, string> = {
  personal_loan: "👤",
  home_loan: "🏠",
  car_loan: "🚗",
  education_loan: "🎓",
  credit_card: "💳",
  other: "💰",
}

const debtSchema = z.object({
  name: z.string().min(1, "Name is required"),
  type: z.enum([
    "personal_loan",
    "home_loan",
    "car_loan",
    "education_loan",
    "credit_card",
    "other",
  ]),
  lender: z.string().optional(),
  principal: z
    .string()
    .min(1)
    .refine((v) => Number(v) > 0, "Must be > 0"),
  outstandingBalance: z
    .string()
    .min(1)
    .refine((v) => Number(v) >= 0, "Must be ≥ 0"),
  interestRate: z.string().optional(),
  emiAmount: z.string().optional(),
  tenureMonths: z.string().optional(),
  startDate: z.string().optional(),
  owner: z.string(),
  notes: z.string().optional(),
})

type DebtForm = z.infer<typeof debtSchema>

const EMPTY_FORM: DebtForm = {
  name: "",
  type: "personal_loan",
  lender: "",
  principal: "",
  outstandingBalance: "",
  interestRate: "",
  emiAmount: "",
  tenureMonths: "",
  startDate: "",
  owner: "joint",
  notes: "",
}

function DebtPage() {
  const ownerLabels = useOwnerLabels()
  const fmt = useCurrencyFormat()
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const fieldId = useId()
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Debt | null>(null)
  const [form, setForm] = useState<DebtForm>(EMPTY_FORM)
  const [errors, setErrors] = useState<Partial<Record<keyof DebtForm, string>>>({})

  const pendingAction = useUIStore((s) => s.pendingAction)
  const setPendingAction = useUIStore((s) => s.setPendingAction)

  useEffect(() => {
    if (pendingAction === "add_debt") {
      setEditing(null)
      setForm(EMPTY_FORM)
      setErrors({})
      setShowForm(true)
      setPendingAction(null)
    }
  }, [pendingAction, setPendingAction])

  const { data, isLoading } = useQuery({
    queryKey: ["debts"],
    queryFn: () => api.get<DebtsResponse>("/debts"),
  })

  const createMutation = useMutation({
    mutationFn: (body: object) => api.post("/debts", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["debts"] })
      closeForm()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: object }) => api.patch(`/debts/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["debts"] })
      closeForm()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/debts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["debts"] }),
  })

  function openAdd() {
    setEditing(null)
    setForm(EMPTY_FORM)
    setErrors({})
    setShowForm(true)
  }

  function openEdit(debt: Debt) {
    setEditing(debt)
    setForm({
      name: debt.name,
      type: debt.type as DebtForm["type"],
      lender: debt.lender ?? "",
      principal: debt.principal,
      outstandingBalance: debt.outstandingBalance,
      interestRate: debt.interestRate ?? "",
      emiAmount: debt.emiAmount ?? "",
      tenureMonths: debt.tenureMonths?.toString() ?? "",
      startDate: debt.startDate ?? "",
      owner: debt.owner,
      notes: debt.notes ?? "",
    })
    setErrors({})
    setShowForm(true)
  }

  function closeForm() {
    setShowForm(false)
    setEditing(null)
    setErrors({})
  }

  function setField<K extends keyof DebtForm>(key: K, value: DebtForm[K]) {
    setForm((f) => ({ ...f, [key]: value }))
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }))
  }

  function submit() {
    const result = debtSchema.safeParse(form)
    if (!result.success) {
      const errs: Partial<Record<keyof DebtForm, string>> = {}
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof DebtForm
        if (!errs[key]) errs[key] = issue.message
      }
      setErrors(errs)
      return
    }
    const body = {
      ...result.data,
      lender: result.data.lender || null,
      interestRate: result.data.interestRate || null,
      emiAmount: result.data.emiAmount || null,
      tenureMonths: result.data.tenureMonths ? Number(result.data.tenureMonths) : null,
      startDate: result.data.startDate || null,
      notes: result.data.notes || null,
    }
    if (editing) {
      updateMutation.mutate({ id: editing.id, body })
    } else {
      createMutation.mutate(body)
    }
  }

  const debts = data?.data ?? []
  const totalOutstanding = data?.totalOutstanding ?? 0
  // Receivables ("owed_to_me") aren't debt: the summary counts liabilities only,
  // matching the API's liabilities-only totalOutstanding.
  const liabilities = debts.filter(isLiability)
  const receivableCount = debts.length - liabilities.length
  const totalEmi = liabilities
    .filter((d) => d.isActive)
    .reduce((s, d) => s + Math.max(0, parseFloat(d.emiAmount ?? "0") || 0), 0)
  const isPending = createMutation.isPending || updateMutation.isPending

  const headline = buildDebtHeadline({ debts, totalOutstanding })
  const mutationError = (createMutation.error || updateMutation.error) as Error | null

  return (
    <>
      <Header
        title="Debt Tracker"
        showPeriod={false}
        action={
          <Button size="touch" className="md:h-8" onClick={openAdd}>
            + Add Debt
          </Button>
        }
      />
      <PageWrapper>
        <div
          data-testid="debt-page"
          data-layout={isDesktop ? "desktop" : "phone"}
          className="flex min-w-0 flex-col gap-4"
        >
          {headline && (
            <AnswerHeadline
              headline={toSignetHeadline(headline)}
              renderValue={renderAnswerAmount}
            />
          )}

          {debts.length > 0 && (
            <StatStrip
              isDesktop={isDesktop}
              items={[
                { label: "Total Outstanding", value: <Amount value={totalOutstanding} /> },
                { label: "Monthly EMIs", value: <Amount value={totalEmi} /> },
                {
                  label: "Active Debts",
                  value: liabilities.length,
                  meta: receivableCount > 0 ? `+ ${receivableCount} owed to you` : undefined,
                },
              ]}
            />
          )}

          {isLoading && (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-28 rounded-xl" />
              ))}
            </div>
          )}

          {!isLoading && debts.length === 0 && (
            <EmptyState
              title="No debts tracked"
              description="Add loans, credit card balances, or any liability to track your debt payoff progress."
              action={
                <Button size="touch" className="md:h-8" onClick={openAdd}>
                  + Add Debt
                </Button>
              }
            />
          )}

          {deleteMutation.isError && (
            <p className="text-caption text-accent-amber">
              {(deleteMutation.error as Error)?.message || "Couldn't remove that debt"}
            </p>
          )}

          {!isLoading && debts.length > 0 && (
            <div data-testid="debt-grid" className="grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-2">
              {debts.map((debt) => {
                const principal = parseFloat(debt.principal)
                const outstanding = parseFloat(debt.outstandingBalance)
                const paid = Math.max(0, principal - outstanding)
                const pctPaid =
                  principal > 0 ? Math.max(0, Math.min(100, (paid / principal) * 100)) : 0

                return (
                  <Card
                    key={debt.id}
                    data-testid="debt-card"
                    size="sm"
                    className="min-w-0 gap-3 px-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="text-title" aria-hidden>
                          {DEBT_TYPE_ICONS[debt.type] ?? "💰"}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-callout font-medium text-text-primary">
                            {debt.name}
                          </p>
                          <p className="truncate text-caption text-text-muted">
                            {DEBT_TYPE_LABELS[debt.type]}
                            {debt.lender && ` · ${debt.lender}`}
                            {debt.interestRate && ` · ${debt.interestRate}% p.a.`}
                          </p>
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        {/* Owed money is neutral ink — not a warning, and never red. */}
                        <Amount
                          value={outstanding}
                          className="block text-callout font-semibold text-text-primary"
                        />
                        <p className="text-caption text-text-muted">
                          {isLiability(debt) ? "outstanding" : "owed to you"}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <div className="flex justify-between text-caption text-text-muted">
                        <span>{fmt(paid)} paid</span>
                        <span>
                          <RatioText>{`${pctPaid.toFixed(0)}%`}</RatioText>
                        </span>
                      </div>
                      <RatioProgress
                        value={pctPaid}
                        aria-label={`${debt.name} repaid`}
                        className="gap-0"
                      />
                      <div className="flex flex-wrap justify-between gap-x-3 text-caption text-text-hint">
                        <span>of {fmt(principal)}</span>
                        {debt.emiAmount && <span>EMI: {fmt(parseFloat(debt.emiAmount))}/mo</span>}
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-1 border-t border-border-subtle pt-2">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(debt)}>
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-text-muted hover:text-accent-red"
                        onClick={() => deleteMutation.mutate(debt.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  </Card>
                )
              })}
            </div>
          )}
        </div>

        {showForm && (
          <ResponsiveSheet
            title={editing ? "Edit Debt" : "Add Debt"}
            testId="debt-sheet"
            onClose={closeForm}
          >
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField label="Name" id={`${fieldId}-name`} error={errors.name}>
                  {(c) => (
                    <Input
                      {...c}
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      placeholder="e.g. HDFC Home Loan"
                    />
                  )}
                </FormField>
                <FormField id={`${fieldId}-type`} label="Type">
                  {(c) => (
                    <Select
                      value={form.type}
                      onValueChange={(v) => setField("type", v as DebtForm["type"])}
                      items={DEBT_TYPE_LABELS}
                    >
                      <SelectTrigger {...c} className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(DEBT_TYPE_LABELS).map(([v, l]) => (
                          <SelectItem key={v} value={v}>
                            {l}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </FormField>
                <FormField label="Lender" id={`${fieldId}-lender`}>
                  {(c) => (
                    <Input
                      {...c}
                      value={form.lender}
                      onChange={(e) => setField("lender", e.target.value)}
                      placeholder="e.g. HDFC Bank"
                    />
                  )}
                </FormField>
                <FormField id={`${fieldId}-owner`} label="Owner">
                  {(c) => (
                    <Select
                      value={form.owner}
                      onValueChange={(v) => setField("owner", v as DebtForm["owner"])}
                      items={ownerLabels}
                    >
                      <SelectTrigger {...c} className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(["user_1", "user_2", "joint"] as const).map((o) => (
                          <SelectItem key={o} value={o}>
                            {ownerLabels[o]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </FormField>
                <FormField
                  label="Principal Amount (₹)"
                  id={`${fieldId}-principal`}
                  error={errors.principal}
                >
                  {(c) => (
                    <Input
                      {...c}
                      type="number"
                      inputMode="decimal"
                      value={form.principal}
                      onChange={(e) => setField("principal", e.target.value)}
                      placeholder="5000000"
                    />
                  )}
                </FormField>
                <FormField
                  label="Outstanding Balance (₹)"
                  id={`${fieldId}-outstanding`}
                  error={errors.outstandingBalance}
                >
                  {(c) => (
                    <Input
                      {...c}
                      type="number"
                      inputMode="decimal"
                      value={form.outstandingBalance}
                      onChange={(e) => setField("outstandingBalance", e.target.value)}
                      placeholder="4200000"
                    />
                  )}
                </FormField>
                <FormField label="Interest Rate (% p.a.)" id={`${fieldId}-rate`}>
                  {(c) => (
                    <Input
                      {...c}
                      type="number"
                      inputMode="decimal"
                      value={form.interestRate}
                      onChange={(e) => setField("interestRate", e.target.value)}
                      placeholder="8.5"
                      step="0.1"
                    />
                  )}
                </FormField>
                <FormField label="EMI Amount (₹)" id={`${fieldId}-emi`}>
                  {(c) => (
                    <Input
                      {...c}
                      type="number"
                      inputMode="decimal"
                      value={form.emiAmount}
                      onChange={(e) => setField("emiAmount", e.target.value)}
                      placeholder="45000"
                    />
                  )}
                </FormField>
                <FormField label="Tenure (months)" id={`${fieldId}-tenure`}>
                  {(c) => (
                    <Input
                      {...c}
                      type="number"
                      inputMode="numeric"
                      value={form.tenureMonths}
                      onChange={(e) => setField("tenureMonths", e.target.value)}
                      placeholder="240"
                    />
                  )}
                </FormField>
                <FormField label="Start Date" id={`${fieldId}-start`}>
                  {(c) => (
                    <Input
                      {...c}
                      type="date"
                      value={form.startDate}
                      onChange={(e) => setField("startDate", e.target.value)}
                    />
                  )}
                </FormField>
              </div>
              <FormField label="Notes" id={`${fieldId}-notes`}>
                {(c) => (
                  <Input
                    {...c}
                    value={form.notes}
                    onChange={(e) => setField("notes", e.target.value)}
                    placeholder="Optional notes"
                  />
                )}
              </FormField>
              <FormActions
                error={mutationError ? mutationError.message || "Something went wrong" : null}
              >
                <Button variant="ghost" onClick={closeForm}>
                  Cancel
                </Button>
                <Button onClick={submit} disabled={isPending}>
                  {isPending ? "Saving…" : editing ? "Update" : "Save debt"}
                </Button>
              </FormActions>
            </div>
          </ResponsiveSheet>
        )}
      </PageWrapper>
    </>
  )
}
